import { createClient } from '@connectrpc/connect'
import {
  useQuery as useConnectQuery,
  useMutation,
  useTransport,
  createConnectQueryKey,
} from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import { useQuery as useTanStackQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'

import {
  listAttachmentFolders,
  createAttachmentFolder,
  renameAttachmentFolder,
  moveAttachmentFolder,
  deleteAttachmentFolder,
  listAttachments,
  createAttachment,
} from '@/gen/cortado/v1/cortado-CortadoService_connectquery'
import {
  ListAttachmentFoldersRequestSchema,
  ListAttachmentsRequestSchema,
  ListAttachmentsResponseSchema,
  CreateAttachmentRequestSchema,
  CortadoService,
} from '@/gen/cortado/v1/cortado_pb'
import type { AttachmentLite } from '@/gen/cortado/v1/attachments_pb'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { isValidUUID } from '@/lib/uuid'
import { readFileAsBase64 } from '@/lib/file-utils'

/**
 * Attachment Library store (CPRO-611)
 *
 * The org attachment library is a tree of {@link AttachmentFolder}s plus
 * attachments parented to the organization (`PARENT_TYPE_ORGANIZATION`,
 * `parentId = organizationId`). Folders form a tree via `parentFolderId`
 * (root folders have no parent). Both `listAttachmentFolders` and
 * `listAttachments` return flat lists, so the tree and the per-folder
 * filter are derived client-side.
 *
 * Convention: a `folderId` of `undefined`/`null` means the attachment is
 * unfiled and lives at the library root. Pass `folderId: null` to view only
 * root attachments, or `folderId: undefined` to view all attachments.
 */

/**
 * Sentinel passed to {@link useLibraryAttachments} / {@link useUploadLibraryAttachment}
 * to indicate the library root (unfiled attachments).
 */
export const LIBRARY_ROOT_FOLDER_ID = null

// Mirrors the backend's maxAttachmentListLimit; fetch additional pages below.
const ATTACHMENT_LIST_PAGE_SIZE = 100

/**
 * Fetch the folder tree for an organization.
 *
 * Returns the flat folder list from the backend; callers build the tree from
 * `parentFolderId`. The list query is the invalidation target for every folder
 * mutation below.
 */
export function useAttachmentFolders(organizationId: string) {
  const request = useMemo(
    () => create(ListAttachmentFoldersRequestSchema, { organizationId }),
    [organizationId]
  )

  const query = useConnectQuery(listAttachmentFolders, request, {
    enabled: isValidUUID(organizationId),
  })

  return {
    folders: query.data?.folders ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/**
 * Fetch all library attachments for an organization, optionally narrowed to a
 * single folder client-side.
 *
 * @param organizationId - org whose library to read
 * @param folderId - `undefined` returns every attachment, `null`
 *   ({@link LIBRARY_ROOT_FOLDER_ID}) returns only root/unfiled attachments, and
 *   a string returns attachments in that folder.
 */
export function useLibraryAttachments(
  organizationId: string,
  folderId?: string | null
) {
  const transport = useTransport()
  const client = useMemo(() => createClient(CortadoService, transport), [transport])
  const requestInput = useMemo(
    () => ({
      organizationId,
      parentType: ParentTypeEnum.PARENT_TYPE_ORGANIZATION,
      parentId: organizationId,
      limit: ATTACHMENT_LIST_PAGE_SIZE,
      offset: 0,
    }),
    [organizationId]
  )
  const queryKey = useMemo(
    () =>
      createConnectQueryKey({
        schema: listAttachments,
        input: requestInput,
        transport,
        cardinality: 'finite',
      }),
    [requestInput, transport]
  )

  const query = useTanStackQuery({
    queryKey,
    enabled: isValidUUID(organizationId),
    queryFn: async ({ signal }) => {
      const attachments: AttachmentLite[] = []
      let totalCount: number | undefined

      do {
        const response = await client.listAttachments(
          create(ListAttachmentsRequestSchema, {
            ...requestInput,
            offset: attachments.length,
          }),
          { signal }
        )

        totalCount = response.totalCount
        attachments.push(...response.attachments)

        if (response.attachments.length === 0) {
          break
        }
      } while (totalCount === undefined || attachments.length < totalCount)

      return create(ListAttachmentsResponseSchema, {
        attachments,
        totalCount: totalCount ?? 0,
      })
    },
  })

  const attachments = useMemo(() => {
    const all = query.data?.attachments ?? []
    if (folderId === undefined) return all
    if (folderId === null) return all.filter((a) => !a.folderId)
    return all.filter((a) => a.folderId === folderId)
  }, [query.data?.attachments, folderId])

  return {
    attachments,
    allAttachments: query.data?.attachments ?? [],
    totalCount: query.data?.totalCount ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    hasLoaded: query.isSuccess || !!query.data,
  }
}

/** Build the connect-query key for the org's `listAttachmentFolders` query. */
function useFoldersQueryKey() {
  const transport = useTransport()
  return () =>
    createConnectQueryKey({
      schema: listAttachmentFolders,
      transport,
      cardinality: undefined,
    })
}

/** Build the connect-query key for `listAttachments` (covers the library list). */
function useAttachmentsQueryKey() {
  const transport = useTransport()
  return () =>
    createConnectQueryKey({
      schema: listAttachments,
      transport,
      cardinality: undefined,
    })
}

/**
 * Create a folder in the org library.
 *
 * Invalidates the folder list so the new folder appears in the tree.
 */
export function useCreateAttachmentFolder() {
  const queryClient = useQueryClient()
  const foldersKey = useFoldersQueryKey()

  const mutation = useMutation(createAttachmentFolder, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: foldersKey() })
    },
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Rename a folder.
 *
 * Invalidates the folder list. Attachment rows carry only `folderId` (not the
 * folder name), so the attachments list does not need invalidation here.
 */
export function useRenameAttachmentFolder() {
  const queryClient = useQueryClient()
  const foldersKey = useFoldersQueryKey()

  const mutation = useMutation(renameAttachmentFolder, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: foldersKey() })
    },
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Move a folder under a new parent (or to the root).
 *
 * Invalidates the folder list so the tree re-parents.
 */
export function useMoveAttachmentFolder() {
  const queryClient = useQueryClient()
  const foldersKey = useFoldersQueryKey()

  const mutation = useMutation(moveAttachmentFolder, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: foldersKey() })
    },
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Delete a folder. The backend cascades: subfolders and contained attachments
 * are removed too, so both the folder list and the attachments list are
 * invalidated.
 */
export function useDeleteAttachmentFolder() {
  const queryClient = useQueryClient()
  const foldersKey = useFoldersQueryKey()
  const attachmentsKey = useAttachmentsQueryKey()

  const mutation = useMutation(deleteAttachmentFolder, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: foldersKey() })
      queryClient.invalidateQueries({ queryKey: attachmentsKey() })
    },
  })

  return {
    mutation,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  }
}

/**
 * Upload a file into the org library, optionally into a folder.
 *
 * Wraps `createAttachment` with `parentType=ORGANIZATION`, `parentId=orgId`,
 * and the given `folderId` (omit / `null` for the library root). Invalidates the
 * attachments list on success.
 *
 * @returns `upload(file)` resolving to the created attachment's lite fields,
 *   plus an `isUploading` flag.
 */
export function useUploadLibraryAttachment(
  organizationId: string,
  folderId?: string | null
) {
  const queryClient = useQueryClient()
  const attachmentsKey = useAttachmentsQueryKey()

  const mutation = useMutation(createAttachment, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: attachmentsKey() })
    },
  })

  const upload = async (
    file: File
  ): Promise<{ id: string; filename: string; sizeBytes: bigint }> => {
    const fileBase64 = await readFileAsBase64(file)
    const request = create(CreateAttachmentRequestSchema, {
      organizationId,
      parentType: ParentTypeEnum.PARENT_TYPE_ORGANIZATION,
      parentId: organizationId,
      filename: file.name,
      contentType: file.type,
      fileBase64,
      folderId: folderId ?? undefined,
    })
    const response = await mutation.mutateAsync(request)
    const attachment = response.attachment
    if (!attachment) throw new Error('Upload failed: no attachment returned')
    return {
      id: attachment.id,
      filename: attachment.filename,
      sizeBytes: attachment.sizeBytes,
    }
  }

  return { upload, isUploading: mutation.isPending }
}

/** Convenience alias for the lite attachment shape used by library consumers. */
export type LibraryAttachment = AttachmentLite
